/**
 * useGamification Hook
 * Manages player XP, Level, Streak, Coins, and Reward calculation
 */
import { useState, useEffect, useCallback } from 'react';

export const QUADRANT_XP = {
  0: 60,  // Q0: Urgent - Do It Now
  1: 50,  // Q1: Urgent - Today
  2: 100, // Q2: High Priority - Long time (Strategic growth gets highest XP)
  3: 20,  // Q3: Personal Notes
};

export const RANK_TITLES = [
  { minLevel: 1, title: 'Novice Planner', badge: '🥉' },
  { minLevel: 6, title: 'Focus Apprentice', badge: '🥈' },
  { minLevel: 16, title: 'Matrix Strategist', badge: '🥇' },
  { minLevel: 31, title: 'Productivity Master', badge: '💎' },
  { minLevel: 51, title: 'Grandmaster of Time', badge: '👑' },
];

export function getRankInfo(level) {
  for (let i = RANK_TITLES.length - 1; i >= 0; i--) {
    if (level >= RANK_TITLES[i].minLevel) {
      return RANK_TITLES[i];
    }
  }
  return RANK_TITLES[0];
}

/**
 * Calculates level and progress from total XP.
 * Level N requires N * 150 XP.
 */
export function calculateLevelFromXP(totalXP) {
  let level = 1;
  let remainingXP = Math.max(0, totalXP);

  while (true) {
    const xpRequiredForNext = level * 150;
    if (remainingXP >= xpRequiredForNext) {
      remainingXP -= xpRequiredForNext;
      level += 1;
    } else {
      return {
        level,
        currentLevelXP: remainingXP,
        nextLevelXP: xpRequiredForNext,
        progressPercent: Math.min(100, Math.round((remainingXP / xpRequiredForNext) * 100)),
      };
    }
  }
}

/**
 * Returns streak multiplier based on streak days.
 */
export function getStreakMultiplier(streak) {
  if (streak >= 30) return 2.0;
  if (streak >= 14) return 1.5;
  if (streak >= 7) return 1.25;
  if (streak >= 3) return 1.1;
  return 1.0;
}

/**
 * Formats a Date object as YYYY-MM-DD in local time
 */
export function getLocalDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DEFAULT_GAMIFICATION = {
  totalXP: 0,
  coins: 0,
  currentStreak: 0,
  highestStreak: 0,
  lastActiveDate: null,
};

export function useGamification() {
  const [gameState, setGameState] = useState(() => {
    try {
      const saved = localStorage.getItem('tm-matrix-gamification');
      return saved ? { ...DEFAULT_GAMIFICATION, ...JSON.parse(saved) } : DEFAULT_GAMIFICATION;
    } catch {
      return DEFAULT_GAMIFICATION;
    }
  });

  const [recentReward, setRecentReward] = useState(null);

  useEffect(() => {
    localStorage.setItem('tm-matrix-gamification', JSON.stringify(gameState));
  }, [gameState]);

  // Update streak on task completion
  const updateStreakOnActive = useCallback((currentStreak, lastActiveDate, todayStr) => {
    if (!lastActiveDate) {
      return { newStreak: 1, isNewDay: true };
    }

    if (lastActiveDate === todayStr) {
      return { newStreak: currentStreak, isNewDay: false };
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = getLocalDateString(yesterday);

    if (lastActiveDate === yesterdayStr) {
      return { newStreak: currentStreak + 1, isNewDay: true };
    }

    // Missed at least one day
    return { newStreak: 1, isNewDay: true };
  }, []);

  /**
   * Called when a task is completed
   */
  const earnTaskReward = useCallback((quadrantId) => {
    const todayStr = getLocalDateString();
    const streakInfo = updateStreakOnActive(gameState.currentStreak, gameState.lastActiveDate, todayStr);
    const multiplier = getStreakMultiplier(streakInfo.newStreak);

    const baseXP = QUADRANT_XP[quadrantId] ?? 30;
    const earnedXP = Math.round(baseXP * multiplier);
    const earnedCoins = Math.max(1, Math.floor(earnedXP / 10));

    const oldLevelInfo = calculateLevelFromXP(gameState.totalXP);
    const newTotalXP = gameState.totalXP + earnedXP;
    const newLevelInfo = calculateLevelFromXP(newTotalXP);
    const didLevelUp = newLevelInfo.level > oldLevelInfo.level;

    const newHighestStreak = Math.max(gameState.highestStreak, streakInfo.newStreak);

    setGameState((prev) => ({
      ...prev,
      totalXP: newTotalXP,
      coins: prev.coins + earnedCoins,
      currentStreak: streakInfo.newStreak,
      highestStreak: newHighestStreak,
      lastActiveDate: todayStr,
    }));

    const reward = {
      earnedXP,
      earnedCoins,
      multiplier,
      didLevelUp,
      newLevel: newLevelInfo.level,
      quadrantId,
      timestamp: Date.now(),
    };

    setRecentReward(reward);
    return reward;
  }, [gameState, updateStreakOnActive]);

  /**
   * Called when a task is uncompleted (deducts base XP safely)
   */
  const revertTaskReward = useCallback((quadrantId) => {
    const baseXP = QUADRANT_XP[quadrantId] ?? 30;
    const coinsToDeduct = Math.max(1, Math.floor(baseXP / 10));

    setGameState((prev) => ({
      ...prev,
      totalXP: Math.max(0, prev.totalXP - baseXP),
      coins: Math.max(0, prev.coins - coinsToDeduct),
    }));
  }, []);

  const levelInfo = calculateLevelFromXP(gameState.totalXP);
  const rankInfo = getRankInfo(levelInfo.level);

  return {
    gameState,
    levelInfo,
    rankInfo,
    recentReward,
    clearRecentReward: () => setRecentReward(null),
    earnTaskReward,
    revertTaskReward,
  };
}
